import React from 'react';

import {
  Box,
  Text,
  VStack,
  HStack,
  Button,
  Icon,
  Alert,
  AlertIcon,
  List,
  ListItem,
  ListIcon,
  FormControl,
  FormLabel,
  FormHelperText,
  Switch,
} from '@chakra-ui/react';
import { FaPlay, FaStop, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';

import { useColors } from './colors';

const ProcessControlPanel = ({
  isRunning,
  canStart,
  readinessItems = [],
  onStart,
  onStop,
  estimatedSkuCount,
  scopeCountDetail = null,
  scopeCountLoading = false,
  directListingUpdate = true,
  onDirectListingUpdateChange,
  embedded = false,
}) => {
  const { textColor, backgroundCardColor, borderColor, secondaryTextColor } = useColors();

  const unmetItems = readinessItems.filter((item) => !item.met);
  const allMet = readinessItems.length > 0 && unmetItems.length === 0;

  const renderScopeEstimate = () => {
    if (estimatedSkuCount == null && !scopeCountLoading) {
      return null;
    }

    const detail = scopeCountDetail;
    const lines = [];

    if (scopeCountLoading) {
      lines.push(
        <Text key="main">
          Estimated scope: <strong>…</strong>
        </Text>,
      );
    } else {
      lines.push(
        <Text key="main">
          Estimated scope: <strong>{estimatedSkuCount.toLocaleString()}</strong> SKU
          {estimatedSkuCount === 1 ? '' : 's'} to process
        </Text>,
      );

      if (detail && detail.totalMatching > detail.eligible) {
        const exclusionParts = [];
        if (detail.excludedOptimized > 0) {
          exclusionParts.push(
            `${detail.excludedOptimized.toLocaleString()} already optimized`,
          );
        }
        if (detail.excludedHalted > 0) {
          exclusionParts.push(`${detail.excludedHalted.toLocaleString()} halted`);
        }
        lines.push(
          <Text key="match" fontSize="xs" color={secondaryTextColor} mt={1}>
            {detail.totalMatching.toLocaleString()} match your filter
            {exclusionParts.length > 0
              ? ` · ${exclusionParts.join(' · ')} (skipped)`
              : ''}
          </Text>,
        );
      } else if (detail && (detail.excludedOptimized > 0 || detail.excludedHalted > 0)) {
        const exclusionParts = [];
        if (detail.excludedOptimized > 0) {
          exclusionParts.push(
            `${detail.excludedOptimized.toLocaleString()} already optimized SKU${detail.excludedOptimized === 1 ? '' : 's'}`,
          );
        }
        if (detail.excludedHalted > 0) {
          exclusionParts.push(
            `${detail.excludedHalted.toLocaleString()} halted SKU${detail.excludedHalted === 1 ? '' : 's'}`,
          );
        }
        lines.push(
          <Text key="excl" fontSize="xs" color={secondaryTextColor} mt={1}>
            {exclusionParts.join(' · ')} excluded from scope
          </Text>,
        );
      }

      if (detail?.cappedByLimit) {
        lines.push(
          <Text key="cap" fontSize="xs" color={secondaryTextColor} mt={1}>
            Capped at max limit of {detail.limit.toLocaleString()} (of{' '}
            {detail.eligible.toLocaleString()} eligible)
          </Text>,
        );
      }
    }

    return lines;
  };

  const body = (
    <>
      {!embedded && (
        <>
          <Text color={textColor} fontSize="md" fontWeight="semibold" mb={1}>
            Main process
          </Text>
          <Text color={secondaryTextColor} fontSize="sm" mb={4}>
            Runs verified fetch, applies item specifics rules, and updates fitment on each SKU catalog record.
            When direct update is on, linked eBay listings are revised from the saved catalog after each successful SKU.
          </Text>
        </>
      )}
      {embedded && (
        <Text color={secondaryTextColor} fontSize="sm" mb={4}>
          Runs verified fetch, applies item specifics rules, and updates fitment on each SKU catalog record.
          When direct update is on, linked eBay listings are revised from the saved catalog after each successful SKU.
        </Text>
      )}

      {(estimatedSkuCount != null || scopeCountLoading) && (
        <Alert status="info" borderRadius="md" mb={4} fontSize="sm">
          <AlertIcon />
          <Box>{renderScopeEstimate()}</Box>
        </Alert>
      )}

      {readinessItems.length > 0 && !isRunning && (
        <Alert
          status={allMet ? 'success' : 'warning'}
          borderRadius="md"
          mb={4}
          alignItems="flex-start"
        >
          <AlertIcon mt={1} />
          <Box w="full">
            <Text fontSize="sm" fontWeight="semibold" mb={1}>
              {allMet ? 'Ready to start' : 'Before you can start'}
            </Text>
            <List spacing={1}>
              {readinessItems.map((item) => (
                <ListItem key={item.id} fontSize="sm" display="flex" alignItems="flex-start">
                  <ListIcon
                    as={item.met ? FaCheckCircle : FaExclamationCircle}
                    color={item.met ? 'green.500' : 'orange.500'}
                    mt={1}
                  />
                  <Text color={item.met ? 'green.700' : undefined}>{item.label}</Text>
                </ListItem>
              ))}
            </List>
          </Box>
        </Alert>
      )}

      <FormControl display="flex" alignItems="center" mb={4}>
        <Switch
          id="direct-listing-update"
          isChecked={directListingUpdate}
          onChange={(e) => onDirectListingUpdateChange?.(e.target.checked)}
          colorScheme="teal"
          isDisabled={isRunning}
        />
        <Box ml={3}>
          <FormLabel htmlFor="direct-listing-update" mb={0} fontSize="sm" color={textColor}>
            Direct update linked listings
          </FormLabel>
          <FormHelperText mt={0} fontSize="xs">
            When on, pushes saved catalog to eBay listings for each optimized SKU (same as Items → Resync
            data). When off, only the SKU catalog files and database are updated.
          </FormHelperText>
        </Box>
      </FormControl>

      <VStack spacing={3} mt={embedded ? 0 : 'auto'}>
        {isRunning ? (
          <Button
            w="full"
            size="lg"
            colorScheme="red"
            leftIcon={<Icon as={FaStop} />}
            onClick={onStop}
          >
            Stop process
          </Button>
        ) : (
          <Button
            w="full"
            size="lg"
            colorScheme="teal"
            leftIcon={<Icon as={FaPlay} />}
            onClick={onStart}
            isDisabled={!canStart}
          >
            Start optimization
          </Button>
        )}

        {canStart && !isRunning && (
          <HStack fontSize="xs" color="green.600" justify="center" w="full">
            <Icon as={FaCheckCircle} />
            <Text>All requirements met — ready to run</Text>
          </HStack>
        )}
      </VStack>
    </>
  );

  if (embedded) {
    return body;
  }

  return (
    <Box
      p={5}
      bg={backgroundCardColor}
      borderRadius="lg"
      borderWidth="1px"
      borderColor={borderColor}
      boxShadow="sm"
      height="100%"
      display="flex"
      flexDirection="column"
    >
      {body}
    </Box>
  );
};

export default ProcessControlPanel;
